// uiarc: avatar (https://uiarc.dev/r/avatar.json) — CSS Modules → Tailwind token sınıfları. Görünüm uiarc ile aynı:
// hairline kenarlı yuvarlak, boyutlar sm 28 / md 36 / lg 48 / xl 88 px, sağ altta durum noktası (yayla girer/çıkar).
// Fotoğraf zaten çözülmüşse anında görünür; hâlâ iniyorsa bulanıklıktan solarak girer. Fotoğraf yoksa ya da
// yüklenemezse baş harfler görünür (yüklenemeyince yumuşakça solar). `prefers-reduced-motion` altında hareket yok.
// Sapmalar: `ArcImage` sağlayıcısı yok (düz `<img>`); baş harfler uiarc'taki "ilk iki sözcük" yerine ilk ve son
// sözcüğün baş harfidir ve `tr-TR` ile büyütülür ("ali veli" → "AV", "ismail" → "İ").
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { useLayoutEffect, useRef, useState, type HTMLAttributes } from 'react'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

type AvatarSize = 'sm' | 'md' | 'lg' | 'xl'
type AvatarStatus = 'online' | 'offline'

const sizeClass: Record<AvatarSize, string> = {
  sm: 'size-7 text-[10px] [--status-size:8px] [--status-border:1.5px]',
  md: 'size-9 text-xs [--status-size:10px] [--status-border:2px]',
  lg: 'size-12 text-sm [--status-size:12px] [--status-border:2px]',
  xl: 'size-22 text-xl [--status-size:14px] [--status-border:2px] [--status-inset:4px]',
}

const statusClass: Record<AvatarStatus, string> = {
  online: 'bg-success',
  offline: 'bg-text-muted',
}

const statusLabel: Record<AvatarStatus, string> = { online: 'çevrimiçi', offline: 'çevrimdışı' }

function firstLetter(word: string | undefined) {
  return word ? (Array.from(word)[0] ?? '') : ''
}

/** İlk sözcüğün ve (birden çok sözcük varsa) son sözcüğün baş harfi, `tr-TR` büyük harfle; boş ad `?`. */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  const letters =
    words.length === 1
      ? firstLetter(words[0])
      : firstLetter(words[0]) + firstLetter(words[words.length - 1])
  return letters.toLocaleUpperCase('tr-TR') || '?'
}

type AvatarProps = Omit<HTMLAttributes<HTMLSpanElement>, 'children'> & {
  /** Tam ad: baş harflerin kaynağı, `aria-label` ve `title`. */
  name: string
  src?: string
  size?: AvatarSize
  status?: AvatarStatus
}

function Avatar({ name, src, size = 'md', status, className, ...props }: AvatarProps) {
  const reduce = useReducedMotion()
  const image = useRef<HTMLImageElement>(null)
  const [failedSrc, setFailedSrc] = useState<string>()
  // Çözülmüş fotoğraf anında görünür; hâlâ iniyorsa bekler, sonra bulanıklıktan solarak girer.
  useLayoutEffect(() => {
    const node = image.current
    if (node && !node.complete) node.dataset.loading = ''
  }, [src])
  const showImage = Boolean(src) && failedSrc !== src
  const label = status ? `${name}, ${statusLabel[status]}` : name

  return (
    <span
      title={name}
      {...props}
      data-slot="avatar"
      data-size={size}
      role="img"
      aria-label={label}
      className={cn(
        'border-border bg-surface-muted text-foreground rounded-pill relative inline-grid flex-none place-items-center overflow-visible border font-medium select-none',
        sizeClass[size],
        className,
      )}
    >
      {showImage ? (
        <img
          key={src}
          ref={image}
          src={src}
          alt=""
          decoding="async"
          onLoad={(event) => {
            delete event.currentTarget.dataset.loading
          }}
          onError={() => setFailedSrc(src)}
          className="ease-enter pointer-events-none absolute inset-0 size-full rounded-[inherit] object-cover transition-[opacity,filter] duration-(--duration-standard) data-[loading]:opacity-0 data-[loading]:blur-[4px] data-[loading]:transition-none motion-reduce:transition-none motion-reduce:data-[loading]:blur-none"
        />
      ) : (
        <motion.span
          aria-hidden="true"
          data-slot="avatar-initials"
          initial={src && !reduce ? { opacity: 0 } : false}
          animate={{ opacity: 1 }}
          transition={{ duration: motionTokens.duration.standard, ease: motionTokens.ease.enter }}
        >
          {initials(name)}
        </motion.span>
      )}
      <AnimatePresence initial={false}>
        {status ? (
          <motion.i
            key={status}
            data-slot="avatar-status"
            data-status={status}
            aria-hidden="true"
            className={cn(
              'border-surface rounded-pill absolute right-(--status-inset,0px) bottom-(--status-inset,0px) z-[2] size-(--status-size) border-(length:--status-border) shadow-[0_0_0_1px_color-mix(in_oklab,var(--border)_70%,transparent)]',
              statusClass[status],
            )}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{
              opacity: 0,
              scale: 0.6,
              transition: { duration: reduce ? 0 : motionTokens.duration.fast },
            }}
            transition={reduce ? { duration: 0 } : motionTokens.spring.snappy}
          />
        ) : null}
      </AnimatePresence>
    </span>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- `initials` saf yardımcı, testte ve çağıranda kullanılır
export { Avatar, initials, type AvatarProps, type AvatarSize, type AvatarStatus }
